import { transparentize } from 'polished';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { getAppParameters } from 'src/AppParameters';
import { IconInvalid, IconValid } from 'src/easyclaim.app.assets';
import { ExclamationSimple } from 'src/easyclaim.app.assets/icons/ExclamationSimple';
import { CustomerConfigurationConsumer } from 'src/easyclaim.app.contexts/CustomerConfigurationContextCore';
import styled from 'styled-components';

import { FeedbackIcon } from '..';
import {
	DocumentAddCommandResult,
	DocumentFeature,
	ValidationResult,
} from '../../easyclaim.api.client.typescript/src/index';
import { messages } from '../../easyclaim.app.internationalization/index';

import FormattedHtmlMessage from './FormattedHtmlMessage';
import { getAdditionalFeedback } from './ImageRecognitionAdditionalFeedback';
import { pictureRejectionDocumentFeatures } from './PictureRejection';
import { ValidationContainer } from './ValidationContainer';

export interface ImageRecognitionFeedback2Props {
	validationId: string;
	uploadResult?: DocumentAddCommandResult;
	onShowExample?: () => void;
	onChangeImage?: () => void;
	feedbackMode: FeedbackMode;
	groupFeedback?: boolean;
}

export enum FeedbackMode {
	Main,
	Modal,
}

export class ImageRecognitionFeedback2 extends React.Component<ImageRecognitionFeedback2Props, {}> {
	private processText = (text: string | null | undefined) => {
		if (text && (text.includes('<br>') || text.includes('<br/>'))) {
			return <span dangerouslySetInnerHTML={{ __html: text }} />;
		}

		return text;
	};

	render() {
		const treatErrorAsWarning =
			this.props.uploadResult &&
			this.props.uploadResult.validationResults &&
			this.props.uploadResult.validationResults.filter(
				(vr) => vr.validationResult === ValidationResult.Error
			) &&
			this.props.uploadResult.validationResults.filter((vr) =>
				pictureRejectionDocumentFeatures.some((e) => e == vr.feature)
			);

		const hasWarnings =
			!!this.props.uploadResult &&
			this.props.uploadResult.validationResults &&
			this.props.uploadResult.validationResults.filter(
				(vr) => vr.validationResult === ValidationResult.Warning
			).length > 0;

		const featuresWithExemplaryPhoto = [
			DocumentFeature.ExpectedNumberPlate,
			DocumentFeature.ExpectedFrontLeftPerspective,
			DocumentFeature.ExpectedFrontRightPerspective,
			DocumentFeature.ExpectedRearLeftPerspective,
			DocumentFeature.ExpectedRearRightPerspective,
			DocumentFeature.UndesiredPhotoOfScreen,
		];
		const hasErrorsWithExemplaryPhoto =
			!!this.props.uploadResult &&
			this.props.uploadResult.validationResults &&
			this.props.uploadResult.validationResults.filter(
				(vr) =>
					vr.validationResult === ValidationResult.Error &&
					featuresWithExemplaryPhoto.some((e) => e == vr.feature)
			).length > 0;

		const validationResults =
			!!this.props.uploadResult && !!this.props.uploadResult.validationResults
				? this.props.uploadResult.validationResults
				: null;

		const appParameters = getAppParameters();
		const lang = appParameters.lang;
		const additionalFeedbackTranslationId = getAdditionalFeedback(
			this.props.feedbackMode,
			validationResults
		);
		const additionalInfoTranslationExists =
			!!messages && !!messages[lang][additionalFeedbackTranslationId];

		const imageRecognitionFeedbacks =
			this.props.groupFeedback === true ? (
				<ImageRecognitionFeedbacks className="feedback-main-container">
					{validationResults &&
						(() => {
							const hasNonCorrectResults = validationResults.some(
								(vr) => vr.validationResult !== ValidationResult.Correct
							);
							const resultsToShow = hasNonCorrectResults
								? validationResults.filter((vr) => vr.validationResult !== ValidationResult.Correct)
								: validationResults;

							return resultsToShow.map((key, index) => (
								<FeedbackContainer className="feedback-container" key={index}>
									<Feedback>{this.processText(key.title)}</Feedback>
								</FeedbackContainer>
							));
						})()}
				</ImageRecognitionFeedbacks>
			) : (
				<ImageRecognitionFeedbacks className="feedback-main-container">
					{validationResults &&
						validationResults.map((key, index) => (
							<FeedbackContainer className="feedback-container" key={index}>
								<Feedback>
									{this.processText(key.title)}
									{treatErrorAsWarning?.some((err) => err === key) ? (
										<FeedbackIcon validationResult={ValidationResult.Warning} />
									) : (
										<FeedbackIcon validationResult={key.validationResult} />
									)}
								</Feedback>
							</FeedbackContainer>
						))}
				</ImageRecognitionFeedbacks>
			);

		const imageRecognitionFeedbacksInfoTexts =
			validationResults &&
			validationResults
				.filter((vr) => !!vr.description)
				.map((key, index) => (
					<InfoText
						key={index}
						className="ImageInfoContainer-FeedbackText"
						dangerouslySetInnerHTML={{ __html: key.description! }}
					/>
				));

		return !!this.props.uploadResult && validationResults && validationResults.length > 0 ? (
			<CustomerConfigurationConsumer>
				{(customerConfig) => {
					if (customerConfig?.configuration?.showUpdatedVerificationResults === true) {
						const overallResult = this.props.uploadResult!.validationResults?.some(
							(vr) => vr.validationResult === ValidationResult.Error
						)
							? ValidationResult.Error
							: this.props.uploadResult!.validationResults?.some(
										(vr) => vr.validationResult === ValidationResult.Warning
								  )
								? ValidationResult.Warning
								: ValidationResult.Correct;

						const feedbackData = validationResults
							? this.props.groupFeedback === true
								? (() => {
										const hasNonCorrectResults = validationResults.some(
											(vr) => vr.validationResult !== ValidationResult.Correct
										);
										const resultsToShow = hasNonCorrectResults
											? validationResults.filter(
													(vr) => vr.validationResult !== ValidationResult.Correct
												)
											: validationResults;

										return resultsToShow.map((vr) => ({
											title: vr.title,
											description: vr.description,
											validationResult: vr.validationResult,
										}));
									})()
								: validationResults.map((vr) => ({
										title: vr.title,
										description: vr.description,
										validationResult: vr.validationResult,
									}))
							: [];

						return (
							<ValidationContainer
								validationId={this.props.validationId}
								validationResult={overallResult}
								blockingValidationResult={
									this.props.uploadResult!.validationResults?.some(
										(vr) => vr.feature === DocumentFeature.ExpectedMileageManualInput
									)
										? ValidationResult.Correct
										: undefined
								}
								hideChangeImage={validationResults.some(
									(vr) => vr.feature === DocumentFeature.ExpectedMileageManualInput
								)}
								hideAcceptImage={validationResults.some(
									(vr) =>
										vr.feature === DocumentFeature.ExpectedMileageManualInput ||
										vr.feature === DocumentFeature.ExpectedMileageRetake
								)}
								hasWarnings={hasWarnings}
								feedbackItems={feedbackData}
								additionalFeedback={
									additionalInfoTranslationExists ? (
										<FormattedHtmlMessage id={additionalFeedbackTranslationId} />
									) : undefined
								}
								onChangeImage={this.props.onChangeImage}
							/>
						);
					}

					return this.props.groupFeedback === true ? (
						<ImageInfoContainer className="ImageInfoContainer">
							{
								<div className="icon-invalid-uniqa">
									{this.props.uploadResult!.validationResults?.some(
										(vr) => vr.validationResult === ValidationResult.Error
									) ? (
										<IconInvalid className="error" />
									) : this.props.uploadResult!.validationResults?.some(
											(vr) => vr.validationResult === ValidationResult.Warning
									  ) ? (
										<ExclamationSimple className="warning" />
									) : this.props.uploadResult!.validationResults?.some(
											(vr) => vr.validationResult === ValidationResult.Correct
									  ) ? (
										<IconValid className="correct" />
									) : null}
								</div>
							}
							{
								<div className="feedback-uniqa">
									{imageRecognitionFeedbacks}
									{imageRecognitionFeedbacksInfoTexts}
								</div>
							}

							{hasWarnings && additionalInfoTranslationExists && (
								<div className="additional-feedback">
									<FormattedHtmlMessage id={additionalFeedbackTranslationId} />
								</div>
							)}
						</ImageInfoContainer>
					) : (
						<ImageInfoContainer className="ImageInfoContainer">
							{imageRecognitionFeedbacks}
							{imageRecognitionFeedbacksInfoTexts}
							{hasWarnings && additionalInfoTranslationExists && (
								<div
									className="additional-feedback"
									style={{
										fontWeight: 'lighter',
										fontStyle: 'italic',
										marginBottom: '1rem',
										marginTop: '1rem',
									}}
								>
									<FormattedHtmlMessage id={additionalFeedbackTranslationId} />
								</div>
							)}
							{this.props.onShowExample && (hasWarnings || hasErrorsWithExemplaryPhoto) && (
								<ActionsContainer>
									<Action
										className="ImageInfoContainer-exemplaryPhoto"
										onClick={this.props.onShowExample}
									>
										<FormattedMessage id="imageRecognitionFeedback2.exemplaryPhoto" />
									</Action>
								</ActionsContainer>
							)}
						</ImageInfoContainer>
					);
				}}
			</CustomerConfigurationConsumer>
		) : null;
	}
}

const ActionsContainer = styled.div`
	display: flex;
	flex-direction: row;
	justify-content: flex-end;
	flex-wrap: wrap;

	& > :not(:first-child) {
		margin-left: 2rem;
	}
`;

const Action = styled.a`
	color: ${(p) => p.theme.colors.primary};
	text-transform: uppercase;
	cursor: pointer;
	text-decoration: none;
	user-select: none;
	background-image: none;
	font-weight: bold;
	font-size: 0.85rem;
`;

const InfoText = styled.div`
	font-weight: lighter;
	font-style: italic;

	margin-top: 1rem;
	margin-bottom: 1rem;
`;

const Feedback = styled.span`
	font-size: 0.9rem;
	font-weight: bolder;
`;
const ImageRecognitionFeedbacks = styled.div``;

const FeedbackContainer = styled.div`
	position: relative;
`;

const ImageInfoContainer = styled.div`
	display: flex;
	flex-direction: column;
	flex-shrink: 0;
	flex-grow: 0;
	padding: 1rem;
	border-radius: 0px 0px 3px 3px;
	background-color: ${(p) => transparentize(0.9, p.theme.colors.secondary)};
`;
